import { forwardRef, memo } from 'react';
import { FormHelperText, InputLabel, type TextareaAutosizeProps } from '@mui/material';
import { type FormItemUiDefinition } from 'types/formItem';
import { getUiDefinitionContainerStyles, getUIDefinitionIconStyles, getUiDefinitionTextStyles } from 'utils/styles';
import { getIcon } from 'utils/common';
import { InputWrapper, StartAdornment, StyledTextArea, Wrapper } from './TextArea.styles';

interface Props extends TextareaAutosizeProps {
  label?: string;
  error?: boolean;
  lightError?: boolean;
  helperText?: string;
  background?: 'transparent' | 'white';
  fullWidth?: boolean;
  /** If undefined, defaults to '100px'. If null, no minimum height is applied. */
  minHeight?: string | null;
  formItemUiDefinition?: FormItemUiDefinition | null;
}

export const TextArea = memo(
  forwardRef<HTMLTextAreaElement, Props>(
    ({ className, label, error, lightError, helperText, fullWidth, background, minHeight, formItemUiDefinition, ...rest }, ref) => {
      const styles = getUiDefinitionTextStyles(formItemUiDefinition);
      const boxStyles = getUiDefinitionContainerStyles(formItemUiDefinition);
      const Icon = formItemUiDefinition?.icon ? getIcon(formItemUiDefinition?.icon ?? '') : null;
      return (
        <Wrapper>
          {!!label && <InputLabel>{label}</InputLabel>}
          <InputWrapper>
            {Icon && (
              <StartAdornment>
                <Icon sx={getUIDefinitionIconStyles(formItemUiDefinition)} />
              </StartAdornment>
            )}
            <StyledTextArea
              ref={ref}
              fullWidth={fullWidth}
              background={background}
              error={error}
              lightError={lightError}
              minHeight={minHeight}
              borderStyles={boxStyles}
              hasAdornment={!!Icon}
              {...rest}
              style={{ ...styles, ...rest.style }}
            />
          </InputWrapper>
          {!!helperText && (
            <FormHelperText error={error} variant="filled">
              {helperText}
            </FormHelperText>
          )}
        </Wrapper>
      );
    }
  )
);
